<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from "vue";
import createDebug from 'debug'
import { isObject, objectHasKey } from '@/helpers'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import type { ZodSchema } from "zod";
import { useField } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";

const debug = createDebug('unifii:InputSelect')

type Item = {
  text: string,
  value: string|number,
  userValue: any
}

type EmitValueSingle = typeof props.itemValue extends undefined ? object : unknown
type EmitValue = typeof props.multiple extends true ? EmitValueSingle[] : EmitValueSingle

const props = withDefaults(
  defineProps<{
    name: string,
    items: object[]

    selected?: unknown
    multiple?: boolean
    itemText?: string | ((value: unknown) => string)
    itemValue?: string | ((value: unknown) => unknown)
    placeholder?: string
    displayLimit?: number
    maxSelected?: number
    rules?: ZodSchema
    inline?: boolean
  }>(), {
    displayLimit: 3,
    placeholder: 'Please select',
  }
)
const { name } = toRefs(props)

const emit = defineEmits<{
  'update:selected': [EmitValue],
  'selected': [EmitValue]
}>()

const $root = ref<HTMLDivElement>()
const isExpanded = ref(false)
const typedRules = computed(() => props.rules ? toTypedSchema(props.rules) : undefined)
const { validate, setValue, errorMessage } = useField(name, typedRules, { controlled: typedRules.value === undefined, syncVModel: false, initialValue: props.selected })

const itemsAsArray = computed<Item[]>(
  () => {
    // Nothing defined.
    if (props.items === undefined) {
      return []
    }

    return props.items.map(
      (value, index): Item|undefined => {
        const itemText2 = extractItemText(value)

        if (itemText2 === undefined) {
          return undefined
        }

        return {
          text: itemText2,
          value: index,
          userValue: value
        }
      }
    ).filter(
      (value): value is Item => value !== undefined
    )
  }
)

function extractItemText(item: unknown): string|undefined {
  const propValue = props.itemText

  if (propValue === undefined) {
    console.warn('The {itemText} prop is required when providing an array of objects.')

    return undefined
  }

  if (typeof propValue === 'function') {
    return propValue(item)
  }

  if (!isObject(item) || !objectHasKey(item, propValue)) {
    console.warn(`The {${propValue}} property doesn't exist on item. Item will be excluded.`)

    return undefined
  }

  return item[propValue]
}

function extractItemValue(item: unknown) {
  const propValue = props.itemValue

  if (propValue === undefined) {
    return item
  }

  if (typeof propValue === 'function') {
    return propValue(item)
  }

  if (!isObject(item)) {
    return item
  }

  if (objectHasKey(item, propValue)) {
    return item[propValue]
  }

  console.warn(`The {${propValue}} property doesn't exist on item.`)

  return undefined
}

const selectedIndexes = computed<number[]>(
  () => {
    const selected = props.selected

    if (selected === undefined) {
      return []
    }

    const selectedValues = (Array.isArray(selected) ? selected : [selected]).map(
      item => {
        return JSON.stringify(extractItemValue(item))
      }
    )

    return itemsAsArray.value.map(
      (item, index) => {
        return selectedValues.indexOf(JSON.stringify(extractItemValue(item.userValue))) > -1
          ? index
          : undefined
      }
    ).filter(
      (index): index is number => index !== undefined
    )
  }
)

// Button text changes depending on how many items are selected.
const buttonText = computed<string>(() => {
  const displayLimit = props.displayLimit;

  if (itemsAsArray.value.length > 0 && selectedIndexes.value.length === itemsAsArray.value.length) {
    return `All selected (${selectedIndexes.value.length})`
  }

  if (selectedIndexes.value.length > displayLimit) {
    return `${selectedIndexes.value.length} selected`
  }

  if (selectedIndexes.value.length > 0) {
    return itemsAsArray.value.filter(
      (item, index) => isSelected(index)
    ).map(
      (item) => item.text
    ).join(', ')
  }

  return props.placeholder
})

function isSelected(index: number) {
  return selectedIndexes.value.indexOf(index) > -1
}

function toggleSelection(index: number) {
  let indexes: number[] = []

  // Multiple selection not allowed. Simply overwrite the selected item indexes.
  if (!props.multiple) {
    debug('Overwriting selected indexes because props.multiple == false.')
    indexes = [index]
  }

  // Copy and splice the selected items, as we're allowing multiple items to be selected.
  else {
    const copiedIndexes = [...selectedIndexes.value]
    const foundIndex = copiedIndexes.indexOf(index)
    const maxSelectedAllowed = props.maxSelected

    if (foundIndex > -1) {
      debug(`Deselecting item with index [${foundIndex}]`)
      copiedIndexes.splice(foundIndex, 1)
    } else if (typeof maxSelectedAllowed === 'number' && copiedIndexes.length >= maxSelectedAllowed) {
      debug(`Ignoring selected item with index [${index}]. We've hit the max number of items allowed to be selected.`)
      return
    } else {
      debug(`Selecting item with index [${index}]`)
      copiedIndexes.push(index)
    }

    indexes = copiedIndexes
  }

  const valuesToEmit: any[] = itemsAsArray.value.filter(
    (item, index) => indexes.indexOf(index) > -1
  ).map(
    (item) => extractItemValue(item.userValue)
  )

  const v = valuesToEmit.length < 1
    ? (props.multiple ? [] : undefined)
    : (props.multiple ? valuesToEmit : valuesToEmit[0])


  setValue(v, false)

  validate().then(
    () => {
      emit('selected', v)
      emit('update:selected', v)
    }
  )
}

onMounted(
  () => {
    $root.value?.addEventListener('show.bs.dropdown', () => isExpanded.value = true)
    $root.value?.addEventListener('hide.bs.dropdown', () => isExpanded.value = false)
  }
)
</script>

<template>
  <div :class="[$style['input-select'], { [$style['input-select-multiple']]: multiple, [$style['input-select-single']]: !multiple, [$style.inline]: inline }]" ref="$root">
    <div class="dropdown" :class="{ 'is-invalid': errorMessage }">
      <button class="d-flex align-items-center justify-content-between btn btn-outline-secondary text-start" :data-bs-toggle="itemsAsArray.length > 0 ? 'dropdown' : undefined">
        {{ buttonText }}
        <FontAwesomeIcon :icon="isExpanded ? faChevronUp : faChevronDown" class="ms-2" />
      </button>
      <ul class="dropdown-menu dropdown-menu-end" @click="multiple && $event.stopPropagation()" v-if="itemsAsArray.length > 0">
        <li :class="{ active: isSelected(index) }" class="dropdown-item d-flex align-items-center" v-for="(item, index) in itemsAsArray" :key="index" @click="toggleSelection(index)">
          <input type="checkbox" class="mt-0 form-check-input" v-if="multiple" :value="item.value" :checked="isSelected(index)" />
          <span :class="{ 'ms-1': multiple }">{{ item.text }}</span>
        </li>
      </ul>
    </div>

    <div class="invalid-tooltip" v-if="errorMessage">{{ errorMessage }}</div>
  </div>
</template>

<style module lang="scss">
@use "@/assets/mixins-form" as mixins;

@import "@/assets/variables";
@import "@/assets/bootstrap-variables";

.input-select {
  width: 100%;
  cursor: default;
  position: relative;

  @include mixins.invalid-tooltip;

  :global {
    .dropdown-menu {
      max-height: 500px;
      overflow-y: auto;
    }

    .dropdown > .btn.show {
      border-color: $input-focus-border-color;
    }

    .is-invalid > button, .is-invalid > .btn.show {
      border-color: var(--#{$prefix}danger);
    }

    .dropdown > .btn {
      --#{$prefix}btn-padding-x: 10px;
      --#{$prefix}btn-padding-y: 6px;

      --#{$prefix}btn-color: #{$body-color};
      --#{$prefix}btn-bg: #fff;
      --#{$prefix}btn-hover-bg: #fff;
      --#{$prefix}btn-hover-color: var(--#{$prefix}btn-color);
      --#{$prefix}btn-hover-border-color: var(--#{$prefix}btn-border-color);
      --#{$prefix}btn-border-color: #d1d1d1;
      --#{$prefix}btn-disabled-border-color: #d1d1d1;
      --#{$prefix}btn-active-bg: var(--#{$prefix}-btn-bg);
      --#{$prefix}btn-active-color: #{$body-color};

      position: relative;
      width: 100%;
      display: none;
    }

    .dropdown > .btn:hover {
      cursor: default;
    }
  }
}

.inline {
  display: inline-block;
  width: auto;

  :global(.dropdown-menu) {
    --bs-dropdown-min-width: auto;
  }
}

.input-select-single {
  :global {
    .dropdown-item:hover, .dropdown-item.active {
      background-color: #e0e0e0;
      color: #000;
    }
  }
}

.input-select-multiple {
  :global {
    .dropdown-item.active {
      color: var(--bs-dropdown-color);
      background-color: transparent;
    }

    .dropdown-item.active:hover, .dropdown-item:active {
      background-color: #e0e0e0;
      color: #000;
    }
  }
}
</style>
